import { createStubImplementation } from '@ardoq/stub-implementation';
import {
  ArdoqId,
  APIWorkspaceAttributes,
  APIModelAttributes,
  APIFieldAttributes,
  APIComponentAttributes,
  APIReferenceAttributes,
  APITagAttributes,
  APIAttachment,
  CreateWorkspaceResponse,
} from '@ardoq/api-types';

export type ScopeCollections = {
  workspaces: APIWorkspaceAttributes[];
  models: APIModelAttributes[];
  fields: APIFieldAttributes[];
  components: APIComponentAttributes[];
  references: APIReferenceAttributes[];
  tags: APITagAttributes[];
  attachments?: APIAttachment[];
};

export type HandleWorkspaceDeleted = (workspaceId: ArdoqId) => void;
export type ResetWithScopeData = (
  scopeData: ScopeCollections,
  options?: { silent: boolean; merge?: boolean }
) => void;
export type ClearCollections = () => void;
export type AddScopeData = (scopeData: ScopeCollections) => void;
export type HandleWorkspaceCreatedTransaction = (
  data: CreateWorkspaceResponse
) => void;

export type DataInterfaceImplementation = {
  handleWorkspaceDeleted: HandleWorkspaceDeleted;
  resetWithScopeData: ResetWithScopeData;
  clearCollections: ClearCollections;
  addScopeData: AddScopeData;
  handleWorkspaceCreatedTransaction: HandleWorkspaceCreatedTransaction;
};

const defaultImplementation: DataInterfaceImplementation = {
  handleWorkspaceDeleted: () => {},
  resetWithScopeData: () => {},
  clearCollections: () => {},
  addScopeData: () => {},
  handleWorkspaceCreatedTransaction: () => {},
};

const {
  registerImplementation,
  handleWorkspaceDeleted,
  resetWithScopeData,
  clearCollections,
  addScopeData,
  handleWorkspaceCreatedTransaction,
} = createStubImplementation('@ardoq/data-interface', defaultImplementation);

/**
 * `dataInterface` is the write side of the Backbone-era model interface layer:
 * every member mutates the global collections imperatively.
 *
 * The app is stream-based now. Dispatch the rxbeach action that owns the change
 * and let the collection routines reduce it, rather than reaching for these
 * mutators from arbitrary code.
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe.
 */
export const dataInterface = {
  defaultImplementation,
  registerImplementation,
  /**
   * @deprecated Dispatch the workspace-deleted action and let the collection routines reduce it.
   */
  handleWorkspaceDeleted,
  /**
   * @deprecated Dispatch the scope-data-loaded action and let the collection routines reduce it.
   */
  resetWithScopeData,
  /**
   * @deprecated Dispatch the action that owns clearing the collections and let the collection routines reduce it.
   */
  clearCollections,
  /**
   * @deprecated Dispatch the scope-data-loaded action and let the collection routines reduce it.
   */
  addScopeData,
  /**
   * @deprecated Dispatch the workspace-created action and let the collection routines reduce it.
   */
  handleWorkspaceCreatedTransaction,
};
